import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNotification } from "./NotificationContext";
import { useShipSettings } from "./ShipSettingsContext";
import { useUserContext } from "./UserContext";
import { useCompanyContext } from "./CompanyContext";
import { useFoundation } from "../application/foundation";
import { getShipCallSign, getShipName } from "../utils/domainSelectors";
import { getSelectedTrip } from "../utils/voyageSelection";
import { createAuditLogInput } from "../utils/auditLog";
import {
  buildOrganizationSeed,
  projectVoyageWorkspace,
  toV2OperationType,
} from "./v2VoyageWorkspace";
import {
  createAuditEntry,
  createSyncOperation,
  createTrip,
  createTripCargo,
  createVoyage,
} from "../domain/entities";
import { ENTITY_TYPES } from "../domain/constants/entityTypes";
import {
  isVoyageEditable,
  isVoyageReadOnly,
} from "../domain/policies/voyageAccessPolicy";
import { runLocalTransaction } from "../data/local/db/runLocalTransaction";

const VoyageContextV2 = createContext(null);

const ACTIVE_VOYAGE_STORAGE_KEY = "activeVoyageId";
const SELECTED_TRIP_STORAGE_KEY = "selectedTripId";
const SELECTED_CARGO_ID_STORAGE_KEY = "selectedCargoId";
const SELECTED_CARGO_INDEX_STORAGE_KEY = "selectedCargoIndex";
const LAST_SYNC_STORAGE_KEY = "lastVoyageSync";

const getStoredSelectedTripId = () => {
  const selectedTripId = localStorage.getItem(SELECTED_TRIP_STORAGE_KEY);
  if (selectedTripId) return selectedTripId;

  const legacySelectedTripIndex = localStorage.getItem("selectedTripIndex");
  if (legacySelectedTripIndex != null) {
    localStorage.setItem(SELECTED_TRIP_STORAGE_KEY, legacySelectedTripIndex);
    localStorage.removeItem("selectedTripIndex");
    return legacySelectedTripIndex;
  }

  return null;
};

const getActorId = (user) => user?.userid || user?.username || "system";

const getStoredSelectedCargoId = () =>
  localStorage.getItem(SELECTED_CARGO_ID_STORAGE_KEY);

const sortVoyages = (voyages) =>
  voyages
    .slice()
    .sort(
      (a, b) =>
        new Date(b.updatedAt || b.createdAt || 0).getTime() -
        new Date(a.updatedAt || a.createdAt || 0).getTime()
    );

const isObject = (value) =>
  value !== null && typeof value === "object" && !Array.isArray(value);

export const VoyageProviderV2 = ({ children }) => {
  const { shipData } = useShipSettings();
  const { user } = useUserContext();
  const { companyName } = useCompanyContext();
  const { showNotification } = useNotification();
  const { localDataAccess, remoteDataAccess, syncEngine, activeContext } =
    useFoundation();
  const { repositories, coordinators } = localDataAccess;
  const {
    setCurrentOrganizationId,
    setCurrentVesselId,
    setCurrentVoyageId: setActiveContextVoyageId,
    setCurrentTripId,
    setSelectedTripCargoId,
  } = activeContext;

  const [currentVoyage, setCurrentVoyage] = useState(null);
  const [shipVoyages, setShipVoyages] = useState([]);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isFetchingCloudVoyages, setIsFetchingCloudVoyages] = useState(false);
  const [lastSynced, setLastSynced] = useState(
    localStorage.getItem(LAST_SYNC_STORAGE_KEY) || null
  );
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [selectedTripId, setSelectedTripIdState] = useState(
    getStoredSelectedTripId()
  );
  const [selectedCargoId, setSelectedCargoIdState] = useState(
    getStoredSelectedCargoId()
  );
  const [selectedCargoIndex, setSelectedCargoIndexState] = useState(
    Number(localStorage.getItem(SELECTED_CARGO_INDEX_STORAGE_KEY)) || 0
  );
  const [currentVoyageId, setCurrentVoyageId] = useState(
    localStorage.getItem(ACTIVE_VOYAGE_STORAGE_KEY)
  );
  const [resolvedScope, setResolvedScope] = useState(null);

  const actorId = getActorId(user);
  const vesselName = getShipName(shipData);

  const ensureFoundationScope = useCallback(async () => {
    if (!shipData?.id || !companyName) return null;

    const organizationSeed = buildOrganizationSeed(companyName);
    let organization = await repositories.organizations.getById(organizationSeed.id);

    if (!organization) {
      const result = await localDataAccess.coordinators.atomicOperationalWrite.commitEntityWrite(
        {
          entity: organizationSeed,
          entityType: ENTITY_TYPES.ORGANIZATION,
          entityRepository: repositories.organizations,
          operation: "create",
          actorId,
          summary: `Bootstrapped organization ${organizationSeed.name}`,
          payload: { organizationId: organizationSeed.id },
          scope: {
            organizationId: organizationSeed.id,
            vesselId: shipData.id,
          },
        }
      );
      organization = result.entity;
    }

    let vessel = await repositories.vessels.getById(shipData.id);

    if (!vessel) {
      const vesselEntity = {
        id: shipData.id,
        organizationId: organization.id,
        name: vesselName || shipData.id,
        imoNumber: shipData?.imoNumber || "",
        callSign: getShipCallSign(shipData) || "",
        vesselType: shipData?.vesselType || "",
        enabledModules: ["coreVoyage", "tankerOperations"],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        schemaVersion: 1,
      };

      const result = await localDataAccess.coordinators.atomicOperationalWrite.commitEntityWrite(
        {
          entity: vesselEntity,
          entityType: ENTITY_TYPES.VESSEL,
          entityRepository: repositories.vessels,
          operation: "create",
          actorId,
          summary: `Bootstrapped vessel ${vesselEntity.name}`,
          payload: { vesselId: vesselEntity.id },
          scope: {
            organizationId: organization.id,
            vesselId: vesselEntity.id,
          },
        }
      );
      vessel = result.entity;
    }

    const scope = {
      organizationId: organization.id,
      vesselId: vessel.id,
    };

    setCurrentOrganizationId(scope.organizationId);
    setCurrentVesselId(scope.vesselId);
    setResolvedScope((previousScope) => {
      if (
        previousScope?.organizationId === scope.organizationId &&
        previousScope?.vesselId === scope.vesselId
      ) {
        return previousScope;
      }

      return scope;
    });

    return scope;
  }, [
    actorId,
    companyName,
    localDataAccess.coordinators.atomicOperationalWrite,
    repositories.organizations,
    repositories.vessels,
    setCurrentOrganizationId,
    setCurrentVesselId,
    shipData,
    vesselName,
  ]);

  const refreshSyncState = useCallback(
    async (scope, voyageId = currentVoyageId) => {
      if (!scope) {
        setHasUnsavedChanges(false);
        return;
      }

      const syncOperations =
        await repositories.syncOperations.listByOrganizationAndVessel(
          scope.organizationId,
          scope.vesselId
        );

      const relevantOperations = voyageId
        ? syncOperations.filter((operation) => operation.voyageId === voyageId)
        : syncOperations;

      setHasUnsavedChanges(
        relevantOperations.some(
          (operation) =>
            operation.status === "pending" || operation.status === "failed"
        )
      );
    },
    [currentVoyageId, repositories.syncOperations]
  );

  const loadVoyageProjection = useCallback(
    async (voyageId, scope) => {
      if (!voyageId || !scope) {
        setCurrentVoyage(null);
        setActiveContextVoyageId(null);
        return null;
      }

      const voyage = await repositories.voyages.getById(voyageId);

      if (!voyage) {
        setCurrentVoyage(null);
        setActiveContextVoyageId(null);
        return null;
      }

      const [trips, vessel] = await Promise.all([
        repositories.trips.listByVoyageId(voyageId),
        repositories.vessels.getById(scope.vesselId),
      ]);

      const cargoEntries = await Promise.all(
        trips.map(async (trip) => [trip.id, await repositories.tripCargo.listByTripId(trip.id)])
      );

      const cargosByTripId = Object.fromEntries(cargoEntries);
      const projectedVoyage = projectVoyageWorkspace({
        voyage,
        vesselName: vessel?.name || vesselName,
        trips,
        cargosByTripId,
      });

      setCurrentVoyage(projectedVoyage);
      setActiveContextVoyageId(voyage.id);
      return projectedVoyage;
    },
    [
      repositories.tripCargo,
      repositories.trips,
      repositories.vessels,
      repositories.voyages,
      setActiveContextVoyageId,
      vesselName,
    ]
  );

  const hydrateRemoteVoyageToLocal = useCallback(
    async (voyageId, scope) => {
      const remoteVoyage =
        await remoteDataAccess.repositories.voyages.getEntity(voyageId);

      if (!remoteVoyage) {
        return false;
      }

      const remoteTrips =
        await remoteDataAccess.repositories.trips.listByVoyageId(voyageId);
      const remoteCargoEntries = await Promise.all(
        remoteTrips.map(async (trip) => [
          trip.id,
          await remoteDataAccess.repositories.tripCargo.listByTripId(trip.id),
        ])
      );
      const remoteCargos = remoteCargoEntries.flatMap(([, cargos]) => cargos);

      await runLocalTransaction(
        localDataAccess.db,
        async () => {
          await repositories.voyages.put(remoteVoyage);
          await repositories.trips.bulkPut(remoteTrips);
          if (remoteCargos.length > 0) {
            await repositories.tripCargo.bulkPut(remoteCargos);
          }
        },
        [
          repositories.voyages.table.name,
          repositories.trips.table.name,
          repositories.tripCargo.table.name,
        ]
      );

      await loadVoyageProjection(voyageId, scope);
      return true;
    },
    [
      loadVoyageProjection,
      localDataAccess.db,
      remoteDataAccess.repositories.tripCargo,
      remoteDataAccess.repositories.trips,
      remoteDataAccess.repositories.voyages,
      repositories.tripCargo,
      repositories.trips,
      repositories.voyages,
    ]
  );

  const refreshVoyages = useCallback(
    async (preferredVoyageId, scopeOverride) => {
      const scope = scopeOverride || resolvedScope || (await ensureFoundationScope());

      if (!scope) {
        setShipVoyages([]);
        setCurrentVoyage(null);
        return [];
      }

      setIsFetchingCloudVoyages(true);

      try {
        const remoteVoyageEntities = sortVoyages(
          await remoteDataAccess.repositories.voyages.listByVesselId(
            scope.vesselId
          )
        );

        const voyageSummaries = remoteVoyageEntities.map((voyage) => ({
          voyageId: voyage.id,
          voyageNumber: voyage.voyageNumber,
          status: voyage.status,
          updatedAt: voyage.updatedAt,
          createdAt: voyage.createdAt,
          endedAt: voyage.completedAt || null,
        }));

        setShipVoyages(voyageSummaries);

        const nextVoyageId =
          preferredVoyageId ||
          currentVoyageId ||
          localStorage.getItem(ACTIVE_VOYAGE_STORAGE_KEY);

        if (nextVoyageId) {
          await loadVoyageProjection(nextVoyageId, scope);
        } else {
          setCurrentVoyage(null);
        }

        await refreshSyncState(scope, nextVoyageId);

        return voyageSummaries;
      } finally {
        setIsFetchingCloudVoyages(false);
      }
    },
    [
      currentVoyageId,
      ensureFoundationScope,
      loadVoyageProjection,
      remoteDataAccess.repositories.voyages,
      refreshSyncState,
      resolvedScope,
    ]
  );

  useEffect(() => {
    ensureFoundationScope()
      .then((scope) => refreshVoyages(currentVoyageId || null, scope))
      .catch((error) => {
        console.error(error);
        showNotification("Failed to initialize voyage workspace", "error");
      });
  }, [currentVoyageId, ensureFoundationScope, refreshVoyages, showNotification]);

  useEffect(() => {
    if (currentVoyageId) {
      localStorage.setItem(ACTIVE_VOYAGE_STORAGE_KEY, currentVoyageId);
    } else {
      localStorage.removeItem(ACTIVE_VOYAGE_STORAGE_KEY);
    }
  }, [currentVoyageId]);

  useEffect(() => {
    if (selectedTripId != null) {
      localStorage.setItem(SELECTED_TRIP_STORAGE_KEY, selectedTripId);
    } else {
      localStorage.removeItem(SELECTED_TRIP_STORAGE_KEY);
    }
  }, [selectedTripId]);

  useEffect(() => {
    if (selectedCargoId) {
      localStorage.setItem(SELECTED_CARGO_ID_STORAGE_KEY, selectedCargoId);
    } else {
      localStorage.removeItem(SELECTED_CARGO_ID_STORAGE_KEY);
    }
  }, [selectedCargoId]);

  useEffect(() => {
    localStorage.setItem(
      SELECTED_CARGO_INDEX_STORAGE_KEY,
      String(selectedCargoIndex)
    );
  }, [selectedCargoIndex]);

  useEffect(() => {
    const tripIds = Object.keys(currentVoyage?.trips || {});
    if (tripIds.length === 0) {
      setCurrentTripId(null);
      setSelectedTripCargoId(null);
      setSelectedCargoIdState(null);
      setSelectedCargoIndexState(0);
      return;
    }

    let normalizedTripId = selectedTripId;
    if (!normalizedTripId || !currentVoyage?.trips?.[normalizedTripId]) {
      normalizedTripId = tripIds[0];
    }

    const selectedTrip = currentVoyage?.trips?.[normalizedTripId];
    const tripCargos = selectedTrip?.cargos || [];

    let normalizedCargo = null;
    if (tripCargos.length > 0) {
      normalizedCargo =
        tripCargos.find((cargo) => cargo.cargoId === selectedCargoId) ||
        tripCargos[Math.min(Math.max(0, selectedCargoIndex || 0), tripCargos.length - 1)] ||
        tripCargos[0];
    }

    const normalizedCargoId = normalizedCargo?.cargoId || null;
    const normalizedCargoIndex = normalizedCargo
      ? tripCargos.findIndex((cargo) => cargo.cargoId === normalizedCargo.cargoId)
      : 0;

    if (normalizedTripId !== selectedTripId) {
      setSelectedTripIdState(normalizedTripId);
    }

    setCurrentTripId(normalizedTripId);

    if (normalizedCargoId !== selectedCargoId) {
      setSelectedCargoIdState(normalizedCargoId);
    }

    if (normalizedCargoIndex !== selectedCargoIndex) {
      setSelectedCargoIndexState(normalizedCargoIndex);
    }

    setSelectedTripCargoId(normalizedCargoId);
  }, [
    currentVoyage,
    selectedCargoId,
    selectedCargoIndex,
    selectedTripId,
    setCurrentTripId,
    setSelectedTripCargoId,
  ]);

  const initNewVoyage = useCallback(
    async (voyageNumberInput) => {
      const scope = resolvedScope || (await ensureFoundationScope());
      if (!scope || !voyageNumberInput.trim()) return false;

      const entity = createVoyage({
        organizationId: scope.organizationId,
        vesselId: scope.vesselId,
        voyageNumber: voyageNumberInput.trim(),
        createdBy: actorId,
      });

      await coordinators.atomicOperationalWrite.commitEntityWrite({
        entity,
        entityType: ENTITY_TYPES.VOYAGE,
        entityRepository: repositories.voyages,
        operation: "create",
        actorId,
        summary: `Created voyage ${entity.voyageNumber}`,
        payload: { voyageId: entity.id },
        scope: {
          ...scope,
          voyageId: entity.id,
        },
      });

      setCurrentVoyageId(entity.id);
      await refreshVoyages(entity.id);
      showNotification(`Created voyage ${entity.voyageNumber}`, "success");
      return true;
    },
    [
      actorId,
      coordinators.atomicOperationalWrite,
      ensureFoundationScope,
      refreshVoyages,
      repositories.voyages,
      resolvedScope,
      showNotification,
    ]
  );

  const updateTrip = useCallback(
    async (tripId, tripPatch = {}, options = {}) => {
      const scope = resolvedScope || (await ensureFoundationScope());
      if (!scope || !currentVoyageId || !tripId || !isObject(tripPatch)) {
        return false;
      }

      const existingTrip = await repositories.trips.getById(tripId);
      if (!existingTrip) return false;
      const voyage = await repositories.voyages.getById(currentVoyageId);
      if (!isVoyageEditable(voyage)) return false;

      const now = new Date().toISOString();
      const updatedTrip = {
        ...existingTrip,
        ...tripPatch,
        id: existingTrip.id,
        voyageId: existingTrip.voyageId,
        sequence: existingTrip.sequence,
        updatedAt: now,
        updatedBy: actorId,
        revision: (existingTrip.revision || 1) + 1,
      };

      await coordinators.atomicOperationalWrite.commitEntityWrite({
        entity: updatedTrip,
        entityType: ENTITY_TYPES.TRIP,
        entityRepository: repositories.trips,
        operation: "update",
        actorId,
        summary:
          options.summary || `Updated trip sequence ${updatedTrip.sequence}`,
        payload: { tripId: updatedTrip.id },
        scope: {
          ...scope,
          voyageId: currentVoyageId,
          tripId: updatedTrip.id,
        },
      });

      await refreshVoyages(currentVoyageId);
      return true;
    },
    [
      actorId,
      coordinators.atomicOperationalWrite,
      currentVoyageId,
      ensureFoundationScope,
      refreshVoyages,
      repositories.trips,
      repositories.voyages,
      resolvedScope,
    ]
  );

  const updateTripCargo = useCallback(
    async (cargoId, cargoPatch = {}, options = {}) => {
      const scope = resolvedScope || (await ensureFoundationScope());
      if (!scope || !currentVoyageId || !cargoId || !isObject(cargoPatch)) {
        return false;
      }

      const existingCargo = await repositories.tripCargo.getById(cargoId);
      if (!existingCargo) return false;
      const voyage = await repositories.voyages.getById(currentVoyageId);
      if (!isVoyageEditable(voyage)) return false;

      const now = new Date().toISOString();
      const updatedCargo = {
        ...existingCargo,
        ...cargoPatch,
        id: existingCargo.id,
        tripId: existingCargo.tripId,
        productName:
          cargoPatch.productName ||
          cargoPatch.cargo?.trim() ||
          existingCargo.productName,
        unit: cargoPatch.unit || existingCargo.unit || "m3 (obs)",
        updatedAt: now,
        updatedBy: actorId,
        revision: (existingCargo.revision || 1) + 1,
      };

      await coordinators.atomicOperationalWrite.commitEntityWrite({
        entity: updatedCargo,
        entityType: ENTITY_TYPES.TRIP_CARGO,
        entityRepository: repositories.tripCargo,
        operation: "update",
        actorId,
        summary: options.summary || `Updated cargo ${updatedCargo.productName}`,
        payload: {
          tripId: existingCargo.tripId,
          tripCargoId: existingCargo.id,
        },
        scope: {
          ...scope,
          voyageId: currentVoyageId,
          tripId: existingCargo.tripId,
        },
      });

      await refreshVoyages(currentVoyageId);
      return true;
    },
    [
      actorId,
      coordinators.atomicOperationalWrite,
      currentVoyageId,
      ensureFoundationScope,
      refreshVoyages,
      repositories.tripCargo,
      repositories.voyages,
      resolvedScope,
    ]
  );

  const saveTrip = useCallback(
    async (tripData) => {
      const scope = resolvedScope || (await ensureFoundationScope());
      if (!scope || !currentVoyageId) return false;

      const voyage = await repositories.voyages.getById(currentVoyageId);
      if (!isVoyageEditable(voyage)) return false;

      const existingTrip = tripData.tripId
        ? await repositories.trips.getById(tripData.tripId)
        : null;
      const allTrips = await repositories.trips.listByVoyageId(currentVoyageId);
      const existingCargos = existingTrip
        ? await repositories.tripCargo.listByTripId(existingTrip.id)
        : [];
      const existingCargoMap = new Map(existingCargos.map((cargo) => [cargo.id, cargo]));

      const maxSequence = allTrips.reduce(
        (highest, trip) => Math.max(highest, trip.sequence || 0),
        0
      );
      const sequence = existingTrip?.sequence || tripData.tripIndex || maxSequence + 1;

      const tripId = existingTrip?.id || tripData.tripId;
      const tripEntity = createTrip({
        id: tripId,
        voyageId: currentVoyageId,
        sequence,
        operationType: toV2OperationType(tripData.operationType),
        status: existingTrip?.status || "active",
        date: tripData.date,
        portNameSnapshot: tripData.to || tripData.from || "",
        jettyNameSnapshot: tripData.jetty || "",
        counterpartyNameSnapshot: tripData.supplierReceiver || "",
        charterPartyDate: tripData.cpDated || "",
        masterSnapshot: {
          name: tripData.crew?.masterName || "",
          role: "master",
        },
        chiefOfficerSnapshot: {
          name: tripData.crew?.chiefOfficer || "",
          role: "chiefofficer",
        },
        createdAt: existingTrip?.createdAt,
        createdBy: existingTrip?.createdBy || actorId,
        updatedBy: actorId,
        revision: existingTrip ? (existingTrip.revision || 1) + 1 : 1,
        amendedAt: existingTrip?.amendedAt,
        amendedBy: existingTrip?.amendedBy,
        amendmentReason: existingTrip?.amendmentReason,
      });

      const validCargos = (tripData.cargos || []).filter(
        (cargo) => cargo.cargo && cargo.cargo.trim()
      );
      const nextCargoIds = new Set(validCargos.map((cargo) => cargo.cargoId));

      const operations = [
        {
          mode: "put",
          entity: tripEntity,
          entityType: ENTITY_TYPES.TRIP,
          entityRepository: repositories.trips,
          operation: existingTrip ? "update" : "create",
          summary: existingTrip
            ? `Updated trip sequence ${tripEntity.sequence}`
            : `Created trip sequence ${tripEntity.sequence}`,
          payload: { tripId: tripEntity.id },
          scope: {
            ...scope,
            voyageId: currentVoyageId,
            tripId: tripEntity.id,
          },
        },
      ];

      validCargos.forEach((cargo) => {
        const existingCargo = existingCargoMap.get(cargo.cargoId);
        operations.push({
          mode: "put",
          entity: createTripCargo({
            id: cargo.cargoId,
            tripId: tripEntity.id,
            productName: cargo.cargo.trim(),
            quantity: cargo.quantity,
            unit: cargo.unit || "m3 (obs)",
            showTolerance: Boolean(cargo.showTolerance),
            tolerances: cargo.tolerances || [],
            createdAt: existingCargo?.createdAt,
            createdBy: existingCargo?.createdBy || actorId,
            updatedBy: actorId,
            revision: existingCargo ? (existingCargo.revision || 1) + 1 : 1,
          }),
          entityType: ENTITY_TYPES.TRIP_CARGO,
          entityRepository: repositories.tripCargo,
          operation: existingCargo ? "update" : "create",
          summary: existingCargo
            ? `Updated cargo ${cargo.cargo.trim()}`
            : `Created cargo ${cargo.cargo.trim()}`,
          payload: { tripCargoId: cargo.cargoId, tripId: tripEntity.id },
          scope: {
            ...scope,
            voyageId: currentVoyageId,
            tripId: tripEntity.id,
          },
        });
      });

      existingCargos
        .filter((cargo) => !nextCargoIds.has(cargo.id))
        .forEach((cargo) => {
          operations.push({
            mode: "delete",
            entityId: cargo.id,
            entityType: ENTITY_TYPES.TRIP_CARGO,
            entityRepository: repositories.tripCargo,
            operation: "delete",
            summary: `Deleted cargo ${cargo.productName}`,
            payload: { tripCargoId: cargo.id, tripId: tripEntity.id },
            scope: {
              ...scope,
              voyageId: currentVoyageId,
              tripId: tripEntity.id,
            },
            entityRevision: (cargo.revision || 1) + 1,
          });
        });

      await coordinators.atomicOperationalWrite.commitBatch({
        actorId,
        operations,
      });

      setSelectedTripIdState(tripEntity.id);
      setSelectedCargoIndexState(0);
      await refreshVoyages(currentVoyageId);
      return true;
    },
    [
      actorId,
      coordinators.atomicOperationalWrite,
      currentVoyageId,
      ensureFoundationScope,
      refreshVoyages,
      repositories.tripCargo,
      repositories.trips,
      repositories.voyages,
      resolvedScope,
    ]
  );

  const amendTrip = useCallback(
    async ({ tripId, amendDate, amendReason }) => {
      const scope = resolvedScope || (await ensureFoundationScope());
      if (!scope || !currentVoyageId || !tripId) return false;

      const voyage = await repositories.voyages.getById(currentVoyageId);
      if (!isVoyageEditable(voyage)) return false;

      const existingTrip = await repositories.trips.getById(tripId);
      if (!existingTrip) return false;

      const amendedTrip = {
        ...existingTrip,
        status: "amended",
        amendedAt: amendDate,
        amendedBy: actorId,
        amendmentReason: amendReason,
        updatedAt: new Date().toISOString(),
        updatedBy: actorId,
        revision: (existingTrip.revision || 1) + 1,
      };

      await coordinators.atomicOperationalWrite.commitEntityWrite({
        entity: amendedTrip,
        entityType: ENTITY_TYPES.TRIP,
        entityRepository: repositories.trips,
        operation: "amend",
        actorId,
        summary: `Amended trip sequence ${existingTrip.sequence}`,
        payload: { tripId, amendmentReason: amendReason },
        scope: {
          ...scope,
          voyageId: currentVoyageId,
          tripId,
        },
      });

      await refreshVoyages(currentVoyageId);
      showNotification("Trip amended", "success");
      return true;
    },
    [
      actorId,
      coordinators.atomicOperationalWrite,
      currentVoyageId,
      ensureFoundationScope,
      refreshVoyages,
      repositories.trips,
      repositories.voyages,
      resolvedScope,
      showNotification,
    ]
  );

  const completeVoyage = useCallback(async () => {
    const scope = resolvedScope || (await ensureFoundationScope());
    if (!scope || !currentVoyageId) return false;

    const existingVoyage = await repositories.voyages.getById(currentVoyageId);
    if (!isVoyageEditable(existingVoyage)) return false;

    const completedVoyage = {
      ...existingVoyage,
      status: "completed",
      completedAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      updatedBy: actorId,
      revision: (existingVoyage.revision || 1) + 1,
    };

    await coordinators.atomicOperationalWrite.commitEntityWrite({
      entity: completedVoyage,
      entityType: ENTITY_TYPES.VOYAGE,
      entityRepository: repositories.voyages,
      operation: "update",
      actorId,
      summary: `Completed voyage ${existingVoyage.voyageNumber}`,
      payload: { voyageId: existingVoyage.id },
      scope: {
        ...scope,
        voyageId: existingVoyage.id,
      },
    });

    await refreshVoyages(currentVoyageId);
    return true;
  }, [
    actorId,
    coordinators.atomicOperationalWrite,
    currentVoyageId,
    ensureFoundationScope,
    refreshVoyages,
    repositories.voyages,
    resolvedScope,
  ]);

  const loadVoyage = useCallback(
    async (voyageId) => {
      if (!voyageId) return false;
      const scope = resolvedScope || (await ensureFoundationScope());
      if (!scope) return false;

      const hydrated = await hydrateRemoteVoyageToLocal(voyageId, scope);
      if (!hydrated) {
        showNotification(`Voyage ${voyageId} not found in cloud`, "error");
        return false;
      }

      setCurrentVoyageId(voyageId);
      await refreshVoyages(voyageId, scope);
      showNotification(`Loaded voyage ${voyageId}`, "success");
      return true;
    },
    [
      ensureFoundationScope,
      hydrateRemoteVoyageToLocal,
      refreshVoyages,
      resolvedScope,
      showNotification,
    ]
  );

  const forceSync = useCallback(async () => {
    setIsSyncing(true);

    try {
      const results = await syncEngine.runPending();
      const failedResults = results.filter((result) => !result.success);

      if (results.length === 0) {
        console.info("[OceanLogix v2 sync] No pending operations to synchronize");
        showNotification("No pending changes to sync", "info");
        return true;
      }

      if (failedResults.length > 0) {
        console.error(
          "[OceanLogix v2 sync] Failed operations",
          failedResults.map(({ operation, error }) => ({
            entityType: operation.entityType,
            entityId: operation.entityId,
            status: operation.status,
            lastError: operation.lastError,
            errorMessage: error?.message || null,
          }))
        );
        await refreshSyncState(resolvedScope);
        showNotification(
          `Sync failed for ${failedResults.length} operation(s)`,
          "error"
        );
        return false;
      }

      const timeString = new Date().toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });
      localStorage.setItem(LAST_SYNC_STORAGE_KEY, timeString);
      setLastSynced(timeString);
      await refreshSyncState(resolvedScope);
      await refreshVoyages(currentVoyageId, resolvedScope);
      console.info("[OceanLogix v2 sync] Synchronized operations", {
        count: results.length,
      });
      showNotification("Data synced to cloud", "success");
      return true;
    } catch (error) {
      console.error(error);
      showNotification("Sync failed", "error");
      return false;
    } finally {
      setIsSyncing(false);
    }
  }, [
    currentVoyageId,
    refreshSyncState,
    refreshVoyages,
    resolvedScope,
    showNotification,
    syncEngine,
  ]);

  const clearLocalVoyageData = useCallback(() => {
    setCurrentVoyageId(null);
    setCurrentVoyage(null);
    setSelectedTripIdState(null);
    setSelectedCargoIdState(null);
    setSelectedCargoIndexState(0);
    setActiveContextVoyageId(null);
    setCurrentTripId(null);
    setSelectedTripCargoId(null);
    localStorage.removeItem(ACTIVE_VOYAGE_STORAGE_KEY);
    localStorage.removeItem(SELECTED_TRIP_STORAGE_KEY);
    localStorage.removeItem(SELECTED_CARGO_ID_STORAGE_KEY);
    localStorage.removeItem("selectedTripIndex");
    localStorage.removeItem(SELECTED_CARGO_INDEX_STORAGE_KEY);
  }, [setActiveContextVoyageId, setCurrentTripId, setSelectedTripCargoId]);

  const setSelectedTripId = useCallback(
    (tripOrIndex) => {
      if (tripOrIndex == null) return;
      let tripId = tripOrIndex;

      if (typeof tripOrIndex === "number") {
        const tripIds = Object.keys(currentVoyage?.trips || {});
        tripId = tripIds[tripOrIndex] || null;
      }

      if (!tripId) return;
      setSelectedTripIdState(tripId);
      setCurrentTripId(tripId);
      setSelectedCargoIdState(null);
      setSelectedCargoIndexState(0);
    },
    [currentVoyage, setCurrentTripId]
  );

  const setSelectedCargoIndex = useCallback(
    (idx) => {
      const cargoIndex = Number.isFinite(Number(idx)) ? Number(idx) : 0;
      const trip = getSelectedTrip(currentVoyage, selectedTripId);
      const cargo = trip?.cargos?.[cargoIndex];
      setSelectedCargoIdState(cargo?.cargoId || null);
      setSelectedCargoIndexState(cargoIndex);
      setSelectedTripCargoId(cargo?.cargoId || null);
    },
    [currentVoyage, selectedTripId, setSelectedTripCargoId]
  );

  const addLog = useCallback(
    async (input) => {
      try {
        const scope = resolvedScope || (await ensureFoundationScope());
        if (!scope) return false;

        const entry = createAuditLogInput(input);
        const targetVoyageId = entry.voyageId || currentVoyageId || null;
        const targetEntityType = entry.cargoId
          ? ENTITY_TYPES.TRIP_CARGO
          : entry.tripId
            ? ENTITY_TYPES.TRIP
            : targetVoyageId
              ? ENTITY_TYPES.VOYAGE
              : ENTITY_TYPES.ORGANIZATION;

        const targetEntityId =
          entry.cargoId || entry.tripId || targetVoyageId || scope.organizationId;

        const auditEntry = createAuditEntry({
          entityType: targetEntityType,
          entityId: targetEntityId,
          operation: "update",
          actorId: entry.user || actorId,
          revision: undefined,
          summary: entry.activity,
          payload: {
            details: entry.details,
            tripId: entry.tripId,
            cargoId: entry.cargoId,
            voyageId: targetVoyageId,
          },
        });

        const syncOperation = createSyncOperation({
          organizationId: scope.organizationId,
          vesselId: scope.vesselId,
          voyageId: targetVoyageId || undefined,
          tripId: entry.tripId || undefined,
          entityType: ENTITY_TYPES.AUDIT_ENTRY,
          entityId: auditEntry.id,
          entityRevision: 1,
          operation: "upsert",
          status: "pending",
        });

        await runLocalTransaction(
          localDataAccess.db,
          async () => {
            await repositories.auditEntries.put(auditEntry);
            await repositories.syncOperations.put(syncOperation);
          },
          [
            repositories.auditEntries.table.name,
            repositories.syncOperations.table.name,
          ]
        );

        return true;
      } catch (error) {
        console.error("Failed to record audit log", error);
        showNotification("Failed to record audit log", "warning");
        return false;
      }
    },
    [
      actorId,
      currentVoyageId,
      ensureFoundationScope,
      localDataAccess.db,
      repositories.auditEntries,
      repositories.syncOperations,
      resolvedScope,
      showNotification,
    ]
  );

  const value = useMemo(
    () => ({
      currentVoyage,
      shipVoyages,
      isSyncing,
      isFetchingCloudVoyages,
      lastSynced,
      hasUnsavedChanges,
      selectedTripId,
      selectedCargoId,
      selectedCargoIndex,
      setSelectedTripId,
      setSelectedCargoIndex,
      updateTrip,
      updateTripCargo,
      initNewVoyage,
      saveTrip,
      amendTrip,
      completeVoyage,
      fetchFromCloud: loadVoyage,
      fetchVoyagesForShip: refreshVoyages,
      forceSync,
      clearLocalVoyageData,
      addLog,
      isVoyageReadOnly: isVoyageReadOnly(currentVoyage),
    }),
    [
      amendTrip,
      addLog,
      clearLocalVoyageData,
      completeVoyage,
      currentVoyage,
      forceSync,
      hasUnsavedChanges,
      initNewVoyage,
      isFetchingCloudVoyages,
      isSyncing,
      lastSynced,
      loadVoyage,
      refreshVoyages,
      saveTrip,
      selectedCargoId,
      selectedCargoIndex,
      selectedTripId,
      setSelectedCargoIndex,
      setSelectedTripId,
      shipVoyages,
      updateTrip,
      updateTripCargo,
    ]
  );

  return (
    <VoyageContextV2.Provider value={value}>{children}</VoyageContextV2.Provider>
  );
};

export const useVoyageContextV2 = () => useContext(VoyageContextV2);
